// src/utils/money.js
// Central money/currency formatter for the storefront.
// Money values come from the API as strings with 2 decimals (e.g. "100.00").
// Always use this helper instead of hardcoding currency codes or `.toFixed(...)`.

export const DEFAULT_CURRENCY = 'EGP';

const LOCALE_MAP = {
    ar: 'ar-EG',
    en: 'en-EG',
};

const resolveLocale = (locale) => {
    const lng = (locale || (typeof document !== 'undefined' ? document.documentElement.lang : '') || 'ar')
        .toString()
        .toLowerCase()
        .split('-')[0];
    return LOCALE_MAP[lng] || LOCALE_MAP.ar;
};

const toNumber = (amount) => {
    if (amount === null || amount === undefined || amount === '') return 0;
    if (typeof amount === 'number') return Number.isFinite(amount) ? amount : 0;
    const parsed = parseFloat(amount);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Format a monetary value with currency symbol/code.
 *
 * @param {string|number} amount       The raw amount (string preferred, e.g. "100.00").
 * @param {string}        [currency]   ISO currency code (default: EGP).
 * @param {string}        [locale]     i18n locale ("ar" | "en"); defaults to <html lang>.
 * @param {object}        [options]    Extra Intl.NumberFormat overrides.
 */
export function formatMoney(amount, currency = DEFAULT_CURRENCY, locale, options = {}) {
    const value = toNumber(amount);
    const resolvedLocale = resolveLocale(locale);
    const resolvedCurrency = currency || DEFAULT_CURRENCY;

    try {
        return new Intl.NumberFormat(resolvedLocale, {
            style: 'currency',
            currency: resolvedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            ...options,
        }).format(value);
    } catch (e) {
        // Fallback: if the runtime rejects the currency code, render plainly.
        return `${resolvedCurrency} ${value.toFixed(2)}`;
    }
}

/**
 * Format a number-only value (no currency symbol) with the same locale rules.
 */
export function formatNumber(amount, locale, options = {}) {
    const value = toNumber(amount);
    const resolvedLocale = resolveLocale(locale);
    return new Intl.NumberFormat(resolvedLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options,
    }).format(value);
}
